import _ from 'lodash';
import axios from 'axios';
import environment from '@src/environment';
import moment from 'moment';
import utils from '@src/utils/utils';
import optimizeCloudinaryUrl from '@src/utils/cloudinary-optimizations';

export const state = {
  changingWeeks: false,
  currentPlanBufferIndex: 1,
  dailySummary: null,
  dishToAdd: null,
  otherDaySelected: false,
  planTripleBuffer: [{}, {}, {}],
  selectedDaySlot: null,
  isCalVisible: false,
  isPlanEmpty: true,
  weekOffset: 0,
  userRequestClearPlan: false,
};

export const getters = {
  currentWeekString: (state) => `Current week is ${state.weekOffset + 1}`,
  previousPlan: (state) => state.planTripleBuffer[getNextIndex(state.currentPlanBufferIndex, false)],
  currentPlan: (state) => state.planTripleBuffer[state.currentPlanBufferIndex],
  nextPlan: (state) => state.planTripleBuffer[getNextIndex(state.currentPlanBufferIndex, true)],
};

export const mutations = {
  setDailySummary(state, value) {
    if (state.userRequestClearPlan) {
      value.planDays.map((day) => {
        day.Breakfast.recipes = [];
        day.Lunch.recipes = [];
        day.Dinner.recipes = [];
        day.Breakfast.meals = [];
        day.Lunch.meals = [];
        day.Dinner.meals = [];
        day.Breakfast.leftovers = [];
        day.Lunch.leftovers = [];
        day.Dinner.leftovers = [];
        day.Breakfast.leftoverOptions = [];
        day.Lunch.leftoverOptions = [];
        day.Dinner.leftoverOptions = [];
        day.aggregatedNutritionDigest = {};
        return day;
      });
    }
    state.dailySummary = value;
  },
  cachePlan(state, { index, value }) {
    const clone = _.cloneDeep(state.planTripleBuffer);
    clone[index] = value;
    state.planTripleBuffer = clone;
  },
  setCurrentPlanBufferIndex(state, value) {
    state.currentPlanBufferIndex = value;
  },
  setSelectedDaySlot(state, value) {
    state.selectedDaySlot = value;
  },
  setWeekOffset(state, value) {
    state.weekOffset = value;
  },
  setDishToAdd(state, value) {
    state.dishToAdd = value;
  },
  setChangingWeeks(state, value) {
    state.changingWeeks = value;
  },
  setIsPlanEmpty(state, value) {
    state.isPlanEmpty = !state.isPlanEmpty;
  },
  setIsCalVisible(state, value) {
    state.isCalVisible = !state.isCalVisible;
  },
  setOtherDaySelected(state, value) {
    state.otherDaySelected = true;
  },
  handleError(state, newValue) {
    this.state.errors.serverError = newValue;
  },
  setUserRequestClearPlan(state, value) {
    state.userRequestClearPlan = value;
  },
};

export const actions = {
  async getPlanGrid(ctx, { today = moment().format('YYYY-MM-DD') + `T00:00:00.000Z`, isPremium = true } = {}) {
    await ctx.dispatch('getCurrentPlanGrid', { today, isPremium });
  },
  async getPlanGridByWeekOffset(ctx, { today = moment().format('YYYY-MM-DD') + `T00:00:00.000Z`, isPremium = true, weekOffset } = {}) {
    await ctx.dispatch('getCurrentPlanGrid', { today, isPremium, weekOffset });
  },
  async getCurrentPlanGrid(ctx, { today = moment().format('YYYY-MM-DD') + `T00:00:00.000Z`, isPremium = true, weekOffset } = {}) {
    const pageParams = `?page={"offsetFromThisWeek": ${
      weekOffset !== undefined ? weekOffset : ctx.state.weekOffset
    }, "today": "${today}"}&isPremium=${isPremium}&leftoversEnabled=true`;

    await axios
      .get(`${environment.PLAN_WEEK_GRID_URL}${pageParams}`)
      .then((res) => {
        res.data.planDays.forEach((plan) => {
          plan.Breakfast.leftoverOptions.map((leftover) => {
            leftover.imageUrl = optimizeCloudinaryUrl(leftover.imageUrl);
            return leftover;
          });
          plan.Breakfast.recipes.map((recipe) => {
            recipe.imageURL = optimizeCloudinaryUrl(recipe.imageURL);
            return recipe;
          });
          plan.Breakfast.leftovers.map((leftover) => {
            leftover.imageURL = optimizeCloudinaryUrl(leftover.imageURL);
            return leftover;
          });
          plan.Lunch.leftoverOptions.map((leftover) => {
            leftover.imageUrl = optimizeCloudinaryUrl(leftover.imageUrl);
            return leftover;
          });
          plan.Lunch.recipes.map((recipe) => {
            recipe.imageURL = optimizeCloudinaryUrl(recipe.imageURL);
            return recipe;
          });
          plan.Lunch.leftovers.map((leftover) => {
            leftover.imageURL = optimizeCloudinaryUrl(leftover.imageURL);
            return leftover;
          });
          plan.Dinner.leftoverOptions.map((leftover) => {
            leftover.imageUrl = optimizeCloudinaryUrl(leftover.imageUrl);
            return leftover;
          });
          plan.Dinner.recipes.map((recipe) => {
            recipe.imageURL = optimizeCloudinaryUrl(recipe.imageURL);
            return recipe;
          });
          plan.Dinner.leftovers.map((leftover) => {
            leftover.imageURL = optimizeCloudinaryUrl(leftover.imageURL);
            return leftover;
          });
        });
        ctx.commit('cachePlan', { index: ctx.state.currentPlanBufferIndex, value: res.data });

        if (ctx.state.dailySummary && ctx.state.dailySummary.day && ctx.state.dailySummary.selectedDate && !ctx.state.otherDaySelected) {
          res.data.day = res.data.planDays.find((day) =>
            moment(day.dateString.substring(0, day.dateString.indexOf('T'))).isSame(moment(ctx.state.dailySummary.selectedDate), 'day')
          );
          res.data.selectedDate = ctx.state.dailySummary.selectedDate;
          ctx.commit('setDailySummary', res.data);
        } else if (ctx.state.dailySummary && ctx.state.dailySummary.day) {
          if (ctx.state.otherDaySelected) {
            res.data.day = res.data.planDays.find((day) =>
              moment(day.dateString.substring(0, day.dateString.indexOf('T'))).isSame(moment(ctx.state.dailySummary.selectedDate), 'day')
            );
            res.data.selectedDate = ctx.state.dailySummary.selectedDate;
            ctx.commit('setDailySummary', res.data);
          } else {
            // TODO: Keeping this for now to handle the desktop calendar functionality
            const dailySummaryClone = _.cloneDeep(ctx.state.dailySummary);
            dailySummaryClone.day = ctx.state.planTripleBuffer[ctx.state.currentPlanBufferIndex].planDays.find(
              (day) => day.dateString === dailySummaryClone.day.dateString
            );
            dailySummaryClone.planDays = ctx.state.planTripleBuffer[ctx.state.currentPlanBufferIndex].planDays;
            ctx.commit('setDailySummary', dailySummaryClone);
          }
        } else {
          if (res.data.planDays) {
            res.data.day = res.data.planDays.find((day) => {
              const dateString = day.dateString.substring(0, day.dateString.indexOf('T'));
              if (moment(dateString).isSame(moment(), 'day')) {
                return day;
              }
            });
            ctx.commit('setDailySummary', res.data);
          }
        }
      })
      .catch((err) => {
        if (err.retry) {
          err.retry.enabled = true;
        }
        ctx.commit('handleError', err);
        console.log(err);
      });
  },
  async getPreviousPlanGrid(ctx, { today = utils.setToMidNight(new Date()).toISOString(), isPremium = true } = {}) {
    const pageParams = `?page={"offsetFromThisWeek": ${ctx.state.weekOffset - 1}, "today": "${today}"}&isPremium=${isPremium}&leftoversEnabled=true`;
    await axios
      .get(`${environment.PLAN_WEEK_GRID_URL}${pageParams}`)
      .then((res) => ctx.commit('cachePlan', { index: getNextIndex(ctx.state.currentPlanBufferIndex, false), value: res.data }))
      .catch((err) => console.log(err));
  },
  async getNextPlanGrid(ctx, { today = utils.setToMidNight(new Date()).toISOString(), isPremium = true } = {}) {
    const pageParams = `?page={"offsetFromThisWeek": ${ctx.state.weekOffset + 1}, "today": "${today}"}&isPremium=${isPremium}&leftoversEnabled=true`;

    await axios
      .get(`${environment.PLAN_WEEK_GRID_URL}${pageParams}`)
      .then((res) => ctx.commit('cachePlan', { index: getNextIndex(ctx.state.currentPlanBufferIndex, true), value: res.data }))
      .catch((err) => console.log(err));
  },
  async getPreviousWeekPlanGrid(ctx) {
    ctx.commit('setChangingWeeks', true);
    const nextIndex = getNextIndex(ctx.state.currentPlanBufferIndex, false);
    ctx.commit('setCurrentPlanBufferIndex', nextIndex);
    ctx.commit('setWeekOffset', ctx.state.weekOffset - 1);
    setTimeout(async () => {
      await ctx.dispatch('getPreviousPlanGrid');
      ctx.commit('setChangingWeeks', false);
    }, 500);
  },
  async getNextWeekPlanGrid(ctx) {
    ctx.commit('setChangingWeeks', true);
    const nextIndex = getNextIndex(ctx.state.currentPlanBufferIndex, true);
    ctx.commit('setCurrentPlanBufferIndex', nextIndex);
    ctx.commit('setWeekOffset', ctx.state.weekOffset + 1);
    setTimeout(async () => {
      await ctx.dispatch('getNextPlanGrid');
      ctx.commit('setChangingWeeks', false);
    }, 500);
  },
  async clearPlan(ctx) {
    return await axios
      .get(environment.CLEAR_USER_PLAN_URL)
      .then((res) => {
        let aux = clearPlanDays(ctx.state);
        ctx.commit('setUserRequestClearPlan', true);
        return res;
      })
      .catch((error) => console.log(error));
  },
  async getPlannedDish(ctx, dishId) {
    return await axios
      .get(`${environment.PLANNED_DISHES_URL}/${dishId}`)
      .then((res) => {
        if (res.data.dishType === 'm') {
          res.data.recipes = res.data.recipes.map((recipe) => {
            return {
              ...recipe,
              dishType: 'r',
            };
          });
        }
        return res.data;
      })
      .catch((err) => console.log(err));
  },
  async reviseServes(ctx, { dishId, payload }) {
    return await axios
      .post(`${environment.PLANNED_DISHES_URL}/${dishId}/revise-serves`, payload)
      .then((res) => res.data)
      .catch((err) => console.log(err));
  },
};

// Private Helpers

function getNextIndex(currentIndex, incrementing = true) {
  let nextIndex = -1;
  if (incrementing) {
    switch (currentIndex) {
      case 0:
        nextIndex = 1;
        break;
      case 1:
        nextIndex = 2;
        break;
      case 2:
        nextIndex = 0;
        break;
    }
  } else {
    switch (currentIndex) {
      case 0:
        nextIndex = 2;
        break;
      case 1:
        nextIndex = 0;
        break;
      case 2:
        nextIndex = 1;
        break;
    }
  }
  return nextIndex;
}

function clearPlanDays(state) {
  if (state.planTripleBuffer && state.planTripleBuffer.planDays) {
    state.planTripleBuffer.map((planTripleBuffer) => {
      planTripleBuffer.planDays = planTripleBuffer.planDays.map((day) => {
        day.Breakfast.recipes = [];
        day.Lunch.recipes = [];
        day.Dinner.recipes = [];
        day.Breakfast.meals = [];
        day.Lunch.meals = [];
        day.Dinner.meals = [];
        day.Breakfast.leftovers = [];
        day.Lunch.leftovers = [];
        day.Dinner.leftovers = [];
        day.Breakfast.leftoverOptions = [];
        day.Lunch.leftoverOptions = [];
        day.Dinner.leftoverOptions = [];
        day.aggregatedNutritionDigest = {};
        return day;
      });
      return planTripleBuffer;
    });
  } else {
    state.dailySummary.planDays.map((day) => {
      day.Breakfast.recipes = [];
      day.Lunch.recipes = [];
      day.Dinner.recipes = [];
      day.Breakfast.meals = [];
      day.Lunch.meals = [];
      day.Dinner.meals = [];
      day.Breakfast.leftovers = [];
      day.Lunch.leftovers = [];
      day.Dinner.leftovers = [];
      day.Breakfast.leftoverOptions = [];
      day.Lunch.leftoverOptions = [];
      day.Dinner.leftoverOptions = [];
      day.aggregatedNutritionDigest = {};
      return day;
    });
  }

  return state;
}
